import React, { useEffect, useRef, useState } from 'react';
import * as Fi from 'react-icons/fi';
import mammoth from 'mammoth';

type IconProps = React.SVGAttributes<SVGElement>;
const FiFileText = Fi.FiFileText as React.FC<IconProps>;
const FiDownload = Fi.FiDownload as React.FC<IconProps>;

export interface DocumentViewerCanvasProps {
  /** URL to the document (PDF or image). If absent, placeholder is shown. */
  url?: string | null;
  /** MIME type or extension hint: pdf, image (png, jpeg, etc.) */
  type?: 'pdf' | 'image' | 'docx' | 'other';
  zoom: number;
  rotation: number;
  fitMode: 'width' | 'page' | 'custom';
  /** Optional page number for PDF (1-based). Reserved for future PDF.js integration. */
  currentPage?: number;
  onDownload: () => void;
  documentName: string;
  hideDownload?: boolean;
}

const IMAGE_EXTENSIONS = /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i;
const PDF_EXTENSIONS = /\.pdf(\?|$)/i;
const DOCX_EXTENSIONS = /\.docx(\?|$)/i;

function getDocType(url: string, name: string): 'pdf' | 'image' | 'docx' | 'other' {
  const lower = (url + name).toLowerCase();
  if (PDF_EXTENSIONS.test(url) || name.toLowerCase().endsWith('.pdf')) return 'pdf';
  if (IMAGE_EXTENSIONS.test(url) || /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name)) return 'image';
  if (DOCX_EXTENSIONS.test(url) || name.toLowerCase().endsWith('.docx')) return 'docx';
  return 'other';
}

const DocumentViewerCanvas: React.FC<DocumentViewerCanvasProps> = ({
  url,
  type: typeProp,
  zoom,
  rotation,
  fitMode,
  currentPage = 1,
  onDownload,
  documentName,
  hideDownload = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const resolvedType = url ? (typeProp ?? getDocType(url, documentName)) : 'other';
  const isPdf = resolvedType === 'pdf';
  const isImage = resolvedType === 'image';
  const isDocx = resolvedType === 'docx';
  const [docxHtml, setDocxHtml] = useState<string | null>(null);
  const [docxLoading, setDocxLoading] = useState(false);
  const [docxError, setDocxError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    if (!url || !isDocx) {
      setDocxHtml(null);
      setDocxLoading(false);
      setDocxError(null);
      return;
    }

    const run = async () => {
      setDocxLoading(true);
      setDocxError(null);
      setDocxHtml(null);

      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`Failed to load DOCX preview (${res.status})`);
        const arrayBuffer = await res.arrayBuffer();
        const result = await mammoth.convertToHtml({ arrayBuffer });
        if (cancelled) return;
        setDocxHtml(result.value);
      } catch (e) {
        if (cancelled) return;
        const msg = e instanceof Error ? e.message : 'Failed to render DOCX preview';
        setDocxError(msg);
      } finally {
        if (cancelled) return;
        setDocxLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [url, isDocx]);

  // For raw PDFs, #page=N works in some viewers; ?page= does not. Use url as-is for iframe.
  const displayUrl = url ?? '';

  if (!url) {
    return (
      <div className="flex-1 min-h-0 h-full flex items-center justify-center bg-slate-100 dark:bg-slate-700/30 rounded-b-xl border-2 border-t-0 border-dashed border-slate-200 dark:border-slate-600 p-8">
        <div className="text-center max-w-md">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-200 dark:bg-slate-600 text-slate-500 dark:text-slate-400 mb-4">
            <FiFileText className="w-8 h-8" />
          </div>
          <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-200 mb-1">No preview available</h3>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
            This file cannot be previewed here.
          </p>
          {!hideDownload && (
            <button
              type="button"
              onClick={onDownload}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-200 dark:bg-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-300 dark:hover:bg-slate-500 transition-colors text-sm font-medium"
            >
              <FiDownload className="w-4 h-4" />
              Download
            </button>
          )}
        </div>
      </div>
    );
  }

  if (isImage) {
    return (
      <div
        ref={containerRef}
        className="flex-1 min-h-0 h-full overflow-auto flex items-center justify-center p-4 bg-slate-200/50 dark:bg-slate-700/40"
        style={{ transform: `rotate(${rotation}deg)` }}
      >
        <img
          src={url}
          alt={documentName}
          className="max-w-full object-contain shadow-lg rounded bg-white"
          style={{
            transform: `scale(${zoom})`,
            transformOrigin: 'center center',
          }}
          draggable={false}
        />
      </div>
    );
  }

  if (isPdf) {
    return (
      <div ref={containerRef} className="flex-1 min-h-0 h-full overflow-auto flex flex-col bg-slate-300/40 dark:bg-slate-700/50 rounded-b-xl">
        <div
          className="flex-1 min-h-0 w-full flex flex-col"
          style={{
            transform: `scale(${zoom}) rotate(${rotation}deg)`,
            transformOrigin: 'top left',
          }}
        >
          <iframe
            key={displayUrl}
            src={displayUrl}
            title={documentName}
            className="w-full flex-1 min-h-0 border-0 rounded-b-xl bg-white dark:bg-slate-800"
          />
        </div>
      </div>
    );
  }

  if (isDocx) {
    if (docxLoading || docxHtml === null) {
      // Show loading while converting (mammoth conversion can take a bit for large docs).
      return (
        <div
          ref={containerRef}
          className="flex-1 min-h-0 h-full overflow-auto flex items-center justify-center bg-slate-200/50 dark:bg-slate-700/40 rounded-b-xl p-6"
        >
          <div className="text-center max-w-md">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-200 dark:bg-slate-600 text-slate-500 dark:text-slate-400 mb-4">
              <FiFileText className="w-8 h-8" />
            </div>
            <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-200 mb-1">Rendering DOCX…</h3>
            <p className="text-sm text-slate-600 dark:text-slate-400">Please wait a moment.</p>
          </div>
        </div>
      );
    }

    if (docxError) {
      return (
        <div
          ref={containerRef}
          className="flex-1 min-h-0 h-full overflow-auto flex items-center justify-center bg-slate-100 dark:bg-slate-700/30 rounded-b-xl border-2 border-t-0 border-dashed border-slate-200 dark:border-slate-600 p-8"
        >
          <div className="text-center max-w-md">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-200 dark:bg-slate-600 text-slate-500 dark:text-slate-400 mb-4">
              <FiFileText className="w-8 h-8" />
            </div>
            <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-200 mb-1">DOCX preview unavailable</h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">{docxError}</p>
            {!hideDownload && (
              <button
                type="button"
                onClick={onDownload}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-200 dark:bg-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-300 dark:hover:bg-slate-500 transition-colors text-sm font-medium"
              >
                <FiDownload className="w-4 h-4" />
                Download
              </button>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="flex-1 min-h-0 h-full overflow-auto flex flex-col bg-slate-200/50 dark:bg-slate-700/40 rounded-b-xl">
        <div
          className="flex-1 min-h-0 w-full"
          style={{
            transform: `scale(${zoom}) rotate(${rotation}deg)`,
            transformOrigin: 'top left',
          }}
        >
          <div
            className="p-6 prose max-w-none text-slate-800 dark:text-slate-100"
            // Mammoth output is HTML; this is the safest way we can render DOCX in-browser.
            dangerouslySetInnerHTML={{ __html: docxHtml }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-0 h-full flex items-center justify-center bg-slate-100 dark:bg-slate-700/30 rounded-b-xl border-2 border-t-0 border-dashed border-slate-200 dark:border-slate-600 p-8">
      <div className="text-center max-w-md">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-200 dark:bg-slate-600 text-slate-500 dark:text-slate-400 mb-4">
          <FiFileText className="w-8 h-8" />
        </div>
        <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-200 mb-1">Preview not supported</h3>
        <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
          This file type cannot be previewed in the browser. You can download it to open in another application.
        </p>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 dark:bg-primary-500 text-white hover:bg-primary-700 dark:hover:bg-primary-600 transition-colors text-sm font-medium"
        >
          <FiDownload className="w-4 h-4" />
          Open / Download
        </a>
      </div>
    </div>
  );
};

export default DocumentViewerCanvas;
